import * as THREE from 'three';
export let matrixUniforms = {
  uScroll: { value: 0 },
  uMouse: { value: new THREE.Vector2(0, 0) }
};
export function initBackgroundScene() {
  const canvas = document.getElementById('bg-canvas');
  if (!canvas) return;
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 0, 22);
  const renderer = new THREE.WebGLRenderer({
    canvas,
    alpha: true,
    antialias: true,
    powerPreference: 'high-performance'
  });
  renderer.setSize(window.innerWidth, window.innerHeight);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  // --- Soft Glowing Particle Texture ---
  const pCanvas = document.createElement('canvas');
  pCanvas.width = 32;
  pCanvas.height = 32;
  const pCtx = pCanvas.getContext('2d');
  const gradient = pCtx.createRadialGradient(16, 16, 0, 16, 16, 16);
  gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
  gradient.addColorStop(0.25, 'rgba(224, 90, 71, 0.85)'); // Terracotta
  gradient.addColorStop(0.65, 'rgba(33, 33, 37, 0.45)');  // Charcoal
  gradient.addColorStop(1, 'rgba(255, 246, 239, 0)');     // Cream fade
  pCtx.fillStyle = gradient;
  pCtx.fillRect(0, 0, 32, 32);
  const particleTexture = new THREE.CanvasTexture(pCanvas);
  // --- 1. Flank Ambient Particles (GPT-6 Astra Deep Space Dust) ---
  const sideCount = 1400;
  const sidePos = new Float32Array(sideCount * 3);
  const sideInitial = new Float32Array(sideCount * 3);
  const sideColors = new Float32Array(sideCount * 3);
  const colorTerracotta = new THREE.Color('#E05A47');
  const colorCharcoal = new THREE.Color('#212125');
  const colorAmber = new THREE.Color('#F6B26B');
  for (let i = 0; i < sideCount; i++) {
    const i3 = i * 3;
    // Bias particles toward left & right flanks (leaving center open for video & logo)
    const side = Math.random() < 0.5 ? -1 : 1;
    const x = side * (5.5 + Math.random() * 18);
    const y = (Math.random() - 0.5) * 22;
    const z = (Math.random() - 0.5) * 14 - 3;
    sidePos[i3] = x;
    sidePos[i3 + 1] = y;
    sidePos[i3 + 2] = z;
    sideInitial[i3] = x;
    sideInitial[i3 + 1] = y;
    sideInitial[i3 + 2] = z;
    const rColor = Math.random();
    const c = rColor > 0.65 ? colorTerracotta : rColor > 0.35 ? colorAmber : colorCharcoal;
    sideColors[i3] = c.r;
    sideColors[i3 + 1] = c.g;
    sideColors[i3 + 2] = c.b;
  }
  const sideGeo = new THREE.BufferGeometry();
  sideGeo.setAttribute('position', new THREE.BufferAttribute(sidePos, 3));
  sideGeo.setAttribute('color', new THREE.BufferAttribute(sideColors, 3));
  const sideMat = new THREE.PointsMaterial({
    size: 0.22,
    map: particleTexture,
    vertexColors: true,
    transparent: true,
    opacity: 0.55,
    depthWrite: false
  });
  const sideMesh = new THREE.Points(sideGeo, sideMat);
  scene.add(sideMesh);
  // --- 2. Logo Tracer Particles (Scanning logo-removedbg.png) ---
  let logoMesh = null;
  let logoPoints = [];
  let logoCurrentPos, logoOriginPos, logoVelocities;
  let logoCount = 0;
  const logoImg = new Image();
  logoImg.src = '/assets/raw/logo-removedbg.png';
  logoImg.onload = () => {
    const scanWidth = 180;
    const scanHeight = Math.round((logoImg.height / logoImg.width) * scanWidth);
    const offscreenCanvas = document.createElement('canvas');
    offscreenCanvas.width = scanWidth;
    offscreenCanvas.height = scanHeight;
    const ctx = offscreenCanvas.getContext('2d');
    ctx.drawImage(logoImg, 0, 0, scanWidth, scanHeight);
    const imgData = ctx.getImageData(0, 0, scanWidth, scanHeight).data;
    // Scan non-transparent pixels with tight step sampling
    for (let y = 0; y < scanHeight; y += 2) {
      for (let x = 0; x < scanWidth; x += 2) {
        const index = (y * scanWidth + x) * 4;
        const alpha = imgData[index + 3];
        if (alpha > 45) {
          // Scale to comfortable background dimension behind presenter
          const posX = (x - scanWidth / 2) * 0.14;
          const posY = -(y - scanHeight / 2) * 0.14 + 1.2;
          const posZ = (Math.random() - 0.5) * 0.4 - 1.5;
          logoPoints.push(posX, posY, posZ);
        }
      }
    }
    logoCount = logoPoints.length / 3;
    logoCurrentPos = new Float32Array(logoPoints);
    logoOriginPos = new Float32Array(logoPoints);
    logoVelocities = new Float32Array(logoCount * 3);
    const logoColors = new Float32Array(logoCount * 3);
    for (let i = 0; i < logoCount; i++) {
      const i3 = i * 3;
      const c = Math.random() > 0.4 ? colorTerracotta : colorAmber;
      logoColors[i3] = c.r;
      logoColors[i3 + 1] = c.g;
      logoColors[i3 + 2] = c.b;
    }
    const logoGeo = new THREE.BufferGeometry();
    logoGeo.setAttribute('position', new THREE.BufferAttribute(logoCurrentPos, 3));
    logoGeo.setAttribute('color', new THREE.BufferAttribute(logoColors, 3));
    const logoMat = new THREE.PointsMaterial({
      size: 0.28,
      map: particleTexture,
      vertexColors: true,
      transparent: true,
      opacity: 0.85,
      depthWrite: false
    });
    logoMesh = new THREE.Points(logoGeo, logoMat);
    scene.add(logoMesh);
  };
  // --- Mouse & Interactive Physics ---
  let targetMouseX = 0;
  let targetMouseY = 0;
  const raycaster = new THREE.Raycaster();
  const mousePlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const mouseWorldPos = new THREE.Vector3();
  window.addEventListener('mousemove', (e) => {
    targetMouseX = (e.clientX / window.innerWidth - 0.5) * 2;
    targetMouseY = -(e.clientY / window.innerHeight - 0.5) * 2;
    raycaster.setFromCamera(new THREE.Vector2(targetMouseX, targetMouseY), camera);
    raycaster.ray.intersectPlane(mousePlane, mouseWorldPos);
  });
  const clock = new THREE.Clock();
  function animate() {
    requestAnimationFrame(animate);
    const time = clock.getElapsedTime();
    matrixUniforms.uMouse.value.x += (targetMouseX - matrixUniforms.uMouse.value.x) * 0.05;
    matrixUniforms.uMouse.value.y += (targetMouseY - matrixUniforms.uMouse.value.y) * 0.05;
    const scrollBoost = matrixUniforms.uScroll.value;
    const totalScroll = matrixUniforms.uScroll.value;
    camera.position.y = -totalScroll * 12;
    camera.position.x = matrixUniforms.uMouse.value.x * 2.5;
    // 1. Animate Logo Particles (Elastic Spring & Mouse Repulsion)
    if (logoMesh && logoCurrentPos) {
      for (let i = 0; i < logoCount; i++) {
        const i3 = i * 3;
        let px = logoCurrentPos[i3];
        let py = logoCurrentPos[i3 + 1];
        let pz = logoCurrentPos[i3 + 2];
        const ox = logoOriginPos[i3];
        const oy = logoOriginPos[i3 + 1];
        const oz = logoOriginPos[i3 + 2];
        // Cursor magnetic push
        const dx = px - mouseWorldPos.x;
        const dy = py - mouseWorldPos.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 3.5) {
          const force = (3.5 - dist) * 0.12;
          logoVelocities[i3] += (dx / dist) * force;
          logoVelocities[i3 + 1] += (dy / dist) * force;
        }
        // Spring tension back to original logo coordinate
        logoVelocities[i3] += (ox - px) * 0.06;
        logoVelocities[i3 + 1] += (oy - py) * 0.06;
        logoVelocities[i3 + 2] += (oz - pz) * 0.06;
        // Damping
        logoVelocities[i3] *= 0.88;
        logoVelocities[i3 + 1] *= 0.88;
        logoVelocities[i3 + 2] *= 0.88;
        // Subtle ambient breathing wave
        const idleWave = Math.sin(time * 2.0 + ox * 1.5) * 0.015;
        // Scroll acceleration
        px += logoVelocities[i3];
        py += logoVelocities[i3 + 1] + idleWave;
        pz += logoVelocities[i3 + 2] - scrollBoost * 14;
        logoCurrentPos[i3] = px;
        logoCurrentPos[i3 + 1] = py;
        logoCurrentPos[i3 + 2] = pz;
      }
      logoMesh.geometry.attributes.position.needsUpdate = true;
      logoMesh.rotation.y = matrixUniforms.uMouse.value.x * 0.18;
      logoMesh.rotation.x = -matrixUniforms.uMouse.value.y * 0.14;
    }
    // 2. Animate Flank Ambient Dust (Continuous Drift)
    const sPos = sideGeo.attributes.position.array;
    for (let i = 0; i < sideCount; i++) {
      const i3 = i * 3;
      sPos[i3 + 1] += Math.sin(time * 0.8 + sPos[i3] * 0.3) * 0.015;
      sPos[i3 + 2] = sideInitial[i3 + 2] - scrollBoost * 8;
    }
    sideGeo.attributes.position.needsUpdate = true;
    sideMesh.position.y = -totalScroll * 8;
    sideMesh.rotation.y = time * 0.03 + totalScroll * 1.5;
    sideMesh.rotation.x = matrixUniforms.uMouse.value.y * 0.12;
    renderer.render(scene, camera);
  }
  animate();
  window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  });
  window.addEventListener('scroll', () => {
    const maxScroll = document.documentElement.scrollHeight - window.innerHeight;
    const currentScroll = window.scrollY;
    if (maxScroll > 0) {
      matrixUniforms.uScroll.value = currentScroll / maxScroll;
    }
  });
}